import { useEffect, useMemo, useState } from 'react';
import {
  getAffilmasterPartnersMap, getAffilmasterPapCampaigns,
  createAffilmasterPartner, updateAffilmasterPartner, pairAffilmasterCampaign, unpairAffilmasterCampaign,
  type PartnerMapRow, type PapCampaignPick,
} from '../api/affilmaster';

const MARKETS = ['CZ', 'SK', 'Global'] as const;
type Tab = 'obecne' | 'kampane' | 'uol';
interface FormState { name: string; ic: string; vat: string; market: string; currency: 'CZK' | 'EUR' }
const EMPTY: FormState = { name: '', ic: '', vat: '', market: 'CZ', currency: 'CZK' };

export function AffilmasterPartnersSettings({ onBack }: { onBack: () => void }) {
  const [clients, setClients] = useState<PartnerMapRow[] | null>(null);
  const [pap, setPap] = useState<PapCampaignPick[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [modalId, setModalId] = useState<number | 'new' | null>(null);
  const [tab, setTab] = useState<Tab>('obecne');
  const [form, setForm] = useState<FormState>(EMPTY);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    getAffilmasterPartnersMap()
      .then((d) => { if (!cancelled) setClients(d); })
      .catch((e: Error) => { if (!cancelled) setError(e?.message || 'Chyba načítání'); });
    return () => { cancelled = true; };
  }, [reload]);

  const client = modalId === 'new' || modalId == null ? null : (clients?.find((c) => c.id === modalId) ?? null);

  function openNew() { setForm(EMPTY); setTab('obecne'); setError(null); setModalId('new'); }
  function openEdit(c: PartnerMapRow) {
    const mkt = (c.market || c.uolCountry || 'CZ');
    setForm({ name: c.name, ic: c.ic || '', vat: c.vat || c.uolDic || '', market: MARKETS.includes(mkt as never) ? mkt : 'CZ', currency: c.billingCurrency === 'EUR' ? 'EUR' : 'CZK' });
    setFilter(''); setTab('obecne'); setError(null); setModalId(c.id);
  }
  function close() { setModalId(null); }

  async function ensurePap() { if (!pap) { try { setPap(await getAffilmasterPapCampaigns()); } catch (e) { setError((e as Error)?.message || 'Chyba načítání kampaní'); } } }

  async function saveObecne() {
    if (form.name.trim().length < 2) { setError('Název klienta musí mít aspoň 2 znaky'); return; }
    setBusy('form'); setError(null);
    const payload = { name: form.name, ic: form.ic || undefined, vat: form.vat || undefined, market: form.market, billingCurrency: form.currency };
    try {
      if (modalId === 'new') {
        const { id } = await createAffilmasterPartner(payload);
        setReload((r) => r + 1); setModalId(id); setTab('kampane'); await ensurePap();
      } else if (typeof modalId === 'number') {
        await updateAffilmasterPartner(modalId, payload); setReload((r) => r + 1);
      }
    } catch (e) { setError((e as Error)?.message || 'Chyba uložení'); }
    finally { setBusy(null); }
  }

  async function toggle(c: PapCampaignPick) {
    if (!client || busy) return;
    setBusy(c.id); setError(null);
    const paired = client.campaigns.find((x) => x.papCampaignId === c.id);
    try {
      if (paired) await unpairAffilmasterCampaign(client.id, paired.id);
      else await pairAffilmasterCampaign(client.id, c.id, c.name);
      setReload((r) => r + 1);
    } catch (e) { setError((e as Error)?.message || 'Chyba párování'); }
    finally { setBusy(null); }
  }

  const filteredPap = useMemo(() => {
    if (!client || !pap) return [];
    const paired = new Set(client.campaigns.map((c) => c.papCampaignId));
    const q = filter.trim().toLowerCase();
    return pap
      .filter((c) => !q || (c.name || '').toLowerCase().includes(q) || c.id.toLowerCase().includes(q))
      .sort((a, b) => { const ap = paired.has(a.id), bp = paired.has(b.id); if (ap !== bp) return ap ? -1 : 1; return (a.name || '').localeCompare(b.name || ''); });
  }, [client, pap, filter]);

  return (
    <>
      <button type="button" className="btn btn-sm btn-link px-0 mb-2" onClick={onBack}>Zpět na Nastavení</button>
      <div className="d-flex align-items-center mb-1" style={{ gap: 12 }}>
        <h5 className="mb-0">Klienti tržeb - Affilmaster</h5>
        <button type="button" className="btn btn-sm btn-primary ms-auto" onClick={openNew}>+ Nový klient</button>
      </div>
      <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
        Firmy platící za reklamu (IČ/DIČ pro UOL) + jejich napárované PaP kampaně. Klikni na klienta pro úpravu.
      </p>

      {error && !modalId && <div className="alert alert-danger py-2">{error}</div>}
      {!clients && !error && <div className="text-muted py-3">Načítám…</div>}

      {clients && (
        <div className="card"><div className="card-body p-0">
          <div className="table-responsive-mobile">
          <table className="table table-sm mb-0 align-middle">
            <thead><tr><th>Klient</th><th>IČ</th><th>DIČ</th><th>Trh</th><th>Měna</th><th>Kampaně</th><th></th></tr></thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} style={{ opacity: c.active ? 1 : 0.55 }}>
                  <td>
                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                      {c.logo && <img src={c.logo} alt="" style={{ width: 22, height: 22, borderRadius: 4, objectFit: 'cover' }} />}
                      <div><strong>{c.name}</strong>{c.uolCompanyName && c.uolCompanyName !== c.name && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{c.uolCompanyName}</div>}</div>
                    </div>
                  </td>
                  <td>{c.ic || <span className="text-muted">-</span>}</td>
                  <td>{c.vat || c.uolDic || <span className="text-muted">-</span>}</td>
                  <td>{c.market || c.uolCountry || <span className="text-muted">-</span>}</td>
                  <td>{c.billingCurrency}</td>
                  <td><span className="badge badge-soft-success">{c.campaigns.length}</span></td>
                  <td className="text-end"><button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openEdit(c)}>Upravit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div></div>
      )}

      {modalId != null && (
        <div className="prototype-modal-backdrop" onClick={close}>
          <div onClick={(e) => e.stopPropagation()} className="card prototype-modal-card" style={{ width: '100%', maxWidth: 720 }}>
            <div className="prototype-modal-head"><div><div className="prototype-eyebrow">KLIENT</div><h2>{modalId === 'new' ? 'Nový klient' : (client?.name ?? 'Klient')}</h2></div><button type="button" className="prototype-modal-close" aria-label="Zavřít" onClick={close}>×</button></div>
            <div className="d-flex border-bottom px-2" style={{ gap: 4 }}>
              {([['obecne', 'Obecné'], ['kampane', 'Kampaně'], ['uol', 'UOL']] as [Tab, string][]).map(([k, label]) => (
                <button key={k} type="button" disabled={modalId === 'new' && k !== 'obecne'} onClick={() => { setTab(k); if (k === 'kampane') ensurePap(); }}
                  className="bg-transparent" style={{ padding: '8px 14px', border: 'none', borderBottom: tab === k ? '2px solid var(--p1-green, #067836)' : '2px solid transparent', color: tab === k ? 'var(--p1-green, #067836)' : '#292929', cursor: modalId === 'new' && k !== 'obecne' ? 'not-allowed' : 'pointer', opacity: modalId === 'new' && k !== 'obecne' ? 0.4 : 1 }}>
                  {label}
                </button>
              ))}
            </div>
            <div className="card-body">
              {error && <div className="alert alert-danger py-2">{error}</div>}

              {tab === 'obecne' && (
                <>
                  {client?.logo && (
                    <div className="mb-3 d-flex align-items-center" style={{ gap: 12 }}>
                      <img src={client.logo} alt="logo" style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover', border: '1px solid #eee' }} />
                      <span className="text-muted" style={{ fontSize: '0.8rem' }}>Logo klienta · nahrání nového se připravuje</span>
                    </div>
                  )}
                  <div className="row g-2">
                    <div className="col-md-12"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Firma / název</label>
                      <input className="form-control form-control-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="např. Allwyn" /></div>
                    <div className="col-md-3"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>IČ</label>
                      <input className="form-control form-control-sm" value={form.ic} onChange={(e) => setForm({ ...form, ic: e.target.value })} /></div>
                    <div className="col-md-3"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>DIČ (i zahr.)</label>
                      <input className="form-control form-control-sm" value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} /></div>
                    <div className="col-md-3"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Trh</label>
                      <select className="form-select form-select-sm" value={form.market} onChange={(e) => setForm({ ...form, market: e.target.value })}>{MARKETS.map((m) => <option key={m}>{m}</option>)}</select></div>
                    <div className="col-md-3"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Měna</label>
                      <select className="form-select form-select-sm" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value as 'CZK' | 'EUR' })}><option>CZK</option><option>EUR</option></select></div>
                  </div>
                  <button type="button" className="btn btn-sm btn-success mt-3" disabled={busy === 'form'} onClick={saveObecne}>
                    {modalId === 'new' ? 'Vytvořit a pokračovat na kampaně' : 'Uložit'}
                  </button>
                </>
              )}

              {tab === 'kampane' && client && (
                <>
                  <div className="d-flex align-items-center mb-2" style={{ gap: 10 }}>
                    <input className="form-control form-control-sm" style={{ maxWidth: 320 }} placeholder="Filtr kampaní…" value={filter} onChange={(e) => setFilter(e.target.value)} />
                    {pap && <span className="text-muted ms-auto" style={{ fontSize: '0.85rem' }}>napárováno {client.campaigns.length} / {pap.length}</span>}
                  </div>
                  {!pap ? <div className="text-muted py-2">Načítám kampaně z PaP…</div> : (
                    <div style={{ maxHeight: '50vh', overflowY: 'auto', border: '1px solid #eee', borderRadius: 6 }}>
                      {filteredPap.map((c) => {
                        const paired = client.campaigns.some((x) => x.papCampaignId === c.id);
                        return (
                          <label key={c.id} className="d-flex align-items-center px-2 py-2" style={{ gap: 10, borderBottom: '1px solid #f3f3f3', cursor: 'pointer', background: paired ? '#f3fbf5' : undefined }}>
                            <input type="checkbox" checked={paired} disabled={busy === c.id} onChange={() => toggle(c)} />
                            <span style={{ flex: 1 }}>{c.name || <span className="text-muted">(bez názvu)</span>} <code className="text-muted" style={{ fontSize: '0.78rem' }}>{c.id}</code></span>
                            {c.status && <span className={`badge ${c.status.toLowerCase() === 'active' ? 'badge-soft-success' : 'bg-secondary'}`}>{c.status}</span>}
                          </label>
                        );
                      })}
                      {filteredPap.length === 0 && <div className="text-muted px-2 py-2">Žádná kampaň neodpovídá filtru.</div>}
                    </div>
                  )}
                </>
              )}

              {tab === 'uol' && client && (
                <>
                  {client.uolContactId || client.uolCompanyName ? (
                    <>
                      <div className="alert alert-success py-2" style={{ fontSize: '0.9rem' }}>
                        ✓ Napojeno na UOL{client.uolCheckedAt ? ` (ověřeno ${client.uolCheckedAt})` : ''}
                      </div>
                      <div className="table-responsive-mobile">
                      <table className="table table-sm mb-3">
                        <tbody>
                          <tr><th style={{ width: 200 }}>Firma (UOL)</th><td>{client.uolCompanyName || '-'}</td></tr>
                          <tr><th>DIČ (UOL)</th><td>{client.uolDic || '-'}</td></tr>
                          <tr><th>Země</th><td>{client.uolCountry || '-'}</td></tr>
                          <tr><th>UOL kontakt</th><td><code>{client.uolContactId || '-'}</code></td></tr>
                        </tbody>
                      </table>
                      </div>
                    </>
                  ) : (
                    <div className="alert alert-warning py-2" style={{ fontSize: '0.9rem' }}>
                      Klient zatím není napojen na UOL. Spárování proběhne přes IČ/DIČ (vyplň je na tabu „Obecné").
                    </div>
                  )}
                  <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                    Seznam <strong>dokladů (faktur)</strong> se zobrazí, jakmile bude P1 napojeno na zdroj dokladů (Partner Portal) - identita (DIČ/kontakt) výše je párovací klíč.
                  </div>
                </>
              )}
            </div>
            <div className="card-footer d-flex"><button type="button" className="btn btn-sm btn-link ms-auto" onClick={close}>Zavřít</button></div>
          </div>
        </div>
      )}
    </>
  );
}
